/**
 * Onboarding Hooks
 * Following customer feature pattern
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { ICommonQueryParams } from "@/types";
import {
  createOnboardingAccount,
  patchAccountFieldApi,
  getOnboardingAccountDetail,
  updateOnboardingAccount,
  updateOnboardingContact,
  deleteOnboardingAccount,
  deactivateOnboardingAccount,
  activateOnboardingAccount,
  saveCustomDiscountConfig,
  getCustomerDiscountConfigApi,
  createCustomerDiscountConfigApi,
  updateCustomerDiscountConfigApi,
  ICustomerDiscountPayload,
  getOnboardingAccountList,
  getOnboardingAccountByEmail,
  getAccountCountByStatusApi,
  getAllAccountCountsByStatusApi,
  sendOnboardingReminderEmail,
  ISendReminderEmailPayload,
} from "../api";
import { IOnboardingForm } from "../types";
import { queryKey } from "../constants";

/**
 * Hook to fetch onboarding account list
 */
export const useOnboardingAccountList = ({
  queryParams,
}: {
  queryParams: ICommonQueryParams;
}) => {
  return useQuery({
    queryKey: [queryKey.getOnboardingAccountList, queryParams],
    queryFn: () => getOnboardingAccountList(queryParams),
  });
};

/**
 * Hook to create a new onboarding account
 */
export const useCreateOnboardingAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IOnboardingForm) => createOnboardingAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOnboardingAccountList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAccountCountByStatus] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAllAccountCountsByStatus] });
      toast.success("Onboarding account created successfully");
    },

  });
};

/**
 * Hook to update an existing onboarding account
 * Invalidates list and count queries on success
 */
export const useUpdateOnboardingAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<IOnboardingForm>;
    }) => updateOnboardingAccount({ id, payload }),
    onSuccess: () => {
      // Invalidate list API to refetch updated data
      queryClient.invalidateQueries({
        queryKey: [queryKey.getOnboardingAccountList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAccountCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllAccountCountsByStatus],
      });
      toast.success("Onboarding account updated successfully");
    },
  });
};

/**
 * Hook to update primary contact information
 * Invalidates list and count queries on success
 */
export const useUpdateOnboardingContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      accountId: string;
      contactId?: string;
      formValues: Partial<IOnboardingForm>;
    }) => updateOnboardingContact(data),
    onSuccess: () => {
      // Invalidate list API to refetch updated contact data
      queryClient.invalidateQueries({
        queryKey: [queryKey.getOnboardingAccountList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAccountCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllAccountCountsByStatus],
      });
      toast.success("Contact information updated successfully");
    },
  });
};

/**
 * Hook to deactivate an onboarding account
 */
export const useDeactivateOnboardingAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; statecode: number; statuscode: number; dogma_deactivationstate: string }) =>
      deactivateOnboardingAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOnboardingAccountList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAccountCountByStatus] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAllAccountCountsByStatus] });
      toast.success("Account deleted successfully");
    },
  });
};

/**
 * Hook to activate an onboarding account
 */
export const useActivateOnboardingAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; statecode: number; statuscode: number; dogma_deactivationstate: null }) =>
      activateOnboardingAccount(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOnboardingAccountList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAccountCountByStatus] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAllAccountCountsByStatus] });
      toast.success("Account activated successfully");
    },
  });
};

/**
 * Hook to delete an onboarding account
 */
export const useDeleteOnboardingAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteOnboardingAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey.getOnboardingAccountList],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAccountCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllAccountCountsByStatus],
      });
      toast.success("Onboarding account deleted successfully");
    },
  });
};

/**
 * Hook to search for an onboarding account by email
 */
export const useGetOnboardingAccountByEmail = () => {
  return useMutation({
    mutationFn: (email: string) => getOnboardingAccountByEmail(email),
  });
};

/**
 * Hook to get account count by status code (optional filter)
 */
export const useAccountCountByStatus = (statuscode?: number, region?: number) => {
  return useQuery({
    queryKey: [queryKey.getAccountCountByStatus, statuscode, region],
    queryFn: () => getAccountCountByStatusApi(statuscode, region),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to get all account counts grouped by status code
 */
export const useAllAccountCountsByStatus = (region?: number) => {
  return useQuery({
    queryKey: [queryKey.getAllAccountCountsByStatus, region],
    queryFn: () => getAllAccountCountsByStatusApi(region),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to save custom discount configuration for an account
 */
export const useGetOnboardingAccountDetail = (id: string | undefined) => {
  return useQuery({
    queryKey: ['onboarding-account-detail', id],
    queryFn: () => getOnboardingAccountDetail(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePatchAccountField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; [key: string]: any }) => patchAccountFieldApi(payload),
    onSuccess: (_data, vars) => {
      const { id, ...fields } = vars;
      queryClient.setQueryData(
        ['onboarding-account-detail', id],
        (prev: any) => prev ? { ...prev, ...fields } : prev,
      );
    },
  });
};

export const useSaveCustomDiscountConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; dogma_customdiscountconfig: string }) =>
      saveCustomDiscountConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOnboardingAccountList] });
      toast.success("Discount configuration saved successfully");
    },
  });
};

/**
 * Hook to export all onboarding accounts matching the current filter as CSV data
 */
export const useExportOnboardingAccounts = () => {
  return useMutation({
    mutationFn: (filter: string) =>
      getOnboardingAccountList({ current: 1, pageSize: 5000, filter }),
  });
};

/**
 * Hook to send onboarding reminder email
 */
export const useSendOnboardingReminder = () => {
  return useMutation({
    mutationFn: (payload: ISendReminderEmailPayload) => sendOnboardingReminderEmail(payload),
    onSuccess: () => {
      toast.success("Reminder email sent successfully");
    },
  });
};

// ─── Customer Discount Config Hooks ──────────────────────────────────────────

export const useGetCustomerDiscountConfig = (accountId: string | undefined) => {
  return useQuery({
    queryKey: ['customer-discount-config', accountId],
    queryFn: () => getCustomerDiscountConfigApi(accountId!),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCustomerDiscountConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICustomerDiscountPayload) => createCustomerDiscountConfigApi(payload),
    onSuccess: (_data, vars) => {
      queryClient.setQueryData(['customer-discount-config', vars.accountId], vars);
      queryClient.invalidateQueries({ queryKey: ['customer-discount-config', vars.accountId] });
      toast.success('Discount configuration saved successfully');
    },
  });
};

export const useUpdateCustomerDiscountConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICustomerDiscountPayload) => updateCustomerDiscountConfigApi(payload),
    onSuccess: (_data, vars) => {
      queryClient.setQueryData(['customer-discount-config', vars.accountId], vars);
      queryClient.invalidateQueries({ queryKey: ['customer-discount-config', vars.accountId] });
      toast.success('Discount configuration updated successfully');
    },
  });
};
